import { useTopology, DataContainer } from '@estream/sdk-browser/react';
import { NodeStatus } from '@estream/sdk-browser';
import type { TopologySnapshot, TopologyNode } from '@estream/sdk-browser';
import './Topology.css';

export default function Topology() {
  const result = useTopology();
  
  return (
    <div className="topology-page animate-in">
      <DataContainer {...result}>
        {(topology: TopologySnapshot) => (
          <>
            {/* Stats Bar */}
            <div className="topology-stats">
              <StatCard 
                label="Total Nodes" 
                value={topology.nodes.length} 
              />
              <StatCard 
                label="Healthy" 
                value={topology.nodes.filter(n => n.status === NodeStatus.Healthy).length}
                status="success"
              />
              <StatCard 
                label="Degraded" 
                value={topology.nodes.filter(n => n.status === NodeStatus.Degraded).length}
                status="warning"
              />
              <StatCard 
                label="Offline" 
                value={topology.nodes.filter(n => n.status === NodeStatus.Offline || n.status === NodeStatus.Error).length}
                status="error"
              />
              <StatCard 
                label="Links" 
                value={topology.links.length}
              />
            </div>
            
            {/* Nodes Table */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Network Nodes</h2>
                <span className="version-badge">v{topology.version.toString()}</span>
              </div>
              
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Node ID</th>
                    <th>Type</th>
                    <th>Provider</th>
                    <th>Region</th>
                    <th>Version</th>
                    <th>Uptime</th>
                  </tr>
                </thead>
                <tbody>
                  {topology.nodes.map((node) => (
                    <NodeRow key={node.id} node={node} />
                  ))}
                  {topology.nodes.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty-state">
                        No nodes connected to the network
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DataContainer>
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  status 
}: { 
  label: string; 
  value: number;
  status?: 'success' | 'warning' | 'error';
}) {
  return (
    <div className={`stat-card ${status || ''}`}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function NodeRow({ node }: { node: TopologyNode }) {
  const statusClass = node.status.toLowerCase();
  const uptime = node.uptimeSeconds 
    ? formatUptime(Number(node.uptimeSeconds))
    : 'N/A';
  
  return (
    <tr>
      <td>
        <span className={`status-dot ${statusClass}`} />
      </td>
      <td>
        <code className="node-id">{node.id.slice(0, 16)}...</code>
      </td>
      <td>
        <span className="badge badge-primary">{node.type}</span>
      </td>
      <td>{node.cloud}</td>
      <td>{node.region}</td>
      <td>
        <code>{node.version || 'unknown'}</code>
      </td>
      <td>{uptime}</td>
    </tr>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
